import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StyledSelect } from '@/components/ui/styled-select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Trash2, UserCheck, UserPlus, AlertCircle, Users, Mail, MessageSquare, Search, Plus, Upload, Globe
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useState } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_PROP = {
  title: '', location: '', city: '', country: '', region: 'IN',
  price: '', bedrooms: '', bathrooms: '', area: '',
  property_type: 'Villa', description: '', image: '', amenities: ''
};

const AddPropertyDialog = ({ open, onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY_PROP);
  const [csv, setCsv] = useState('');
  const [mode, setMode] = useState('single'); // 'single' | 'csv'
  const [busy, setBusy] = useState(false);

  const submitSingle = async () => {
    if (!form.title || !form.location || !form.price) { toast.error('Title, location and price are required'); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        bedrooms: parseInt(form.bedrooms || 0, 10),
        bathrooms: parseInt(form.bathrooms || 0, 10),
        area: parseInt(form.area || 0, 10),
        amenities: form.amenities ? form.amenities.split('|').map(a => a.trim()).filter(Boolean) : [],
        images: form.image ? [form.image] : [],
      };
      await axios.post(`${API}/admin/properties`, payload);
      toast.success('Property created');
      setForm(EMPTY_PROP);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create property');
    } finally { setBusy(false); }
  };
  const submitCsv = async () => {
    if (!csv.trim()) { toast.error('Paste CSV content first'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/properties/bulk-csv`, { csv });
      const errCount = (r.data.errors || []).length;
      toast.success(`Imported ${r.data.inserted} · skipped ${r.data.skipped_duplicates} · errors ${errCount}`);
      if (errCount) console.warn('CSV row errors', r.data.errors);
      setCsv('');
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk import failed');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-obsidian border border-white/10 text-text-primary max-w-2xl" data-testid="add-property-dialog">
        <DialogHeader><DialogTitle className="font-playfair">Add Property</DialogTitle></DialogHeader>
        <div className="flex gap-2 mb-4">
          <Button variant={mode === 'single' ? 'default' : 'outline'} onClick={() => setMode('single')} className={mode === 'single' ? 'bg-gold text-black' : 'border-white/20'} data-testid="add-prop-mode-single"><Plus className="w-4 h-4 mr-2" /> Single</Button>
          <Button variant={mode === 'csv' ? 'default' : 'outline'} onClick={() => setMode('csv')} className={mode === 'csv' ? 'bg-gold text-black' : 'border-white/20'} data-testid="add-prop-mode-csv"><Upload className="w-4 h-4 mr-2" /> CSV</Button>
        </div>
        {mode === 'single' ? (
          <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-2">
            <div className="col-span-2"><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="bg-white text-black" data-testid="prop-title" /></div>
            <div><Label>Location *</Label><Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Mumbai / Dubai Marina" className="bg-white text-black" data-testid="prop-location" /></div>
            <div><Label>City</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="bg-white text-black" /></div>
            <div><Label>Region *</Label>
              <StyledSelect value={form.region} onChange={(v) => setForm({ ...form, region: v })} options={[{value:'IN',label:'India (IN)'},{value:'UAE',label:'UAE (Dubai)'}]} className="mt-1" data-testid="prop-region" />
            </div>
            <div><Label>Price (AED) *</Label><Input type="number" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} className="bg-white text-black" data-testid="prop-price" /></div>
            <div><Label>Bedrooms</Label><Input type="number" value={form.bedrooms} onChange={e => setForm({ ...form, bedrooms: e.target.value })} className="bg-white text-black" /></div>
            <div><Label>Bathrooms</Label><Input type="number" value={form.bathrooms} onChange={e => setForm({ ...form, bathrooms: e.target.value })} className="bg-white text-black" /></div>
            <div><Label>Area (sqft)</Label><Input type="number" value={form.area} onChange={e => setForm({ ...form, area: e.target.value })} className="bg-white text-black" /></div>
            <div><Label>Type</Label>
              <StyledSelect value={form.property_type || 'Villa'} onChange={(v) => setForm({ ...form, property_type: v })} options={['Villa','Apartment','Penthouse','Mansion','Townhouse']} className="mt-1" data-testid="prop-type" />
            </div>
            <div className="col-span-2"><Label>Image URL</Label><Input value={form.image} onChange={e => setForm({ ...form, image: e.target.value })} placeholder="https://…" className="bg-white text-black" /></div>
            <div className="col-span-2"><Label>Amenities (pipe-separated, e.g. Pool|Gym|Concierge)</Label><Input value={form.amenities} onChange={e => setForm({ ...form, amenities: e.target.value })} className="bg-white text-black" /></div>
            <div className="col-span-2"><Label>Description</Label><textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full bg-white text-black rounded px-3 py-2 mt-1 text-sm" /></div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-text-secondary">Paste CSV with header row. Required: <code className="text-gold">title, location, price</code>. Optional: city, country, region (IN|UAE), bedrooms, bathrooms, area, property_type, description, image, amenities (pipe-sep).</p>
            <textarea rows={12} value={csv} onChange={e => setCsv(e.target.value)} className="w-full bg-black/40 border border-white/10 text-text-primary rounded p-3 font-mono text-xs" data-testid="bulk-csv-textarea"
              placeholder="title,location,city,region,price,bedrooms,bathrooms,area,property_type,image,amenities,description&#10;Worli Penthouse,Mumbai,Mumbai,IN,2848000,4,5,4200,Penthouse,https://…,Pool|Gym|Concierge,Sea-view luxury" />
          </div>
        )}
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} className="border-white/20">Cancel</Button>
          <Button onClick={mode === 'single' ? submitSingle : submitCsv} disabled={busy} className="bg-gold text-black hover:bg-gold-light" data-testid="add-prop-submit">
            {busy ? 'Working…' : (mode === 'single' ? 'Create Property' : 'Import CSV')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AdminPropertiesTab = ({ properties, handleDeleteProperty, openAssignModal, refresh }) => {
  const [open, setOpen] = useState(false);
  return (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div className="text-sm text-text-secondary">{properties.length} properties</div>
      <Button onClick={() => setOpen(true)} className="bg-gold text-black hover:bg-gold-light" data-testid="open-add-property-btn">
        <Plus className="w-4 h-4 mr-2" /> Add Property
      </Button>
    </div>
    <AddPropertyDialog open={open} onClose={() => setOpen(false)} onSaved={refresh} />
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {properties.map(prop => (
        <div key={prop.id} className="glass-card p-4" data-testid={`admin-prop-${prop.id}`}>
          <img src={prop.images?.[0] || 'https://images.unsplash.com/photo-1606917454663-343e58811bb1?w=400'} alt={prop.title} className="w-full h-40 object-cover rounded mb-4" />
          <div className="flex items-start justify-between gap-2 mb-1">
            <h4 className="font-semibold flex-1">{prop.title}</h4>
            <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${prop.region === 'IN' ? 'bg-orange-500/20 text-orange-400' : 'bg-emerald-500/20 text-emerald-400'}`}>{prop.region || '?'}</span>
          </div>
          <p className="text-text-secondary text-sm mb-2"><Globe className="inline w-3 h-3 mr-1" />{prop.location}</p>
          {prop.assigned_partner_name ? (
            <div className="mb-3 p-2 rounded bg-emerald-500/10 border border-emerald-500/30"><div className="flex items-center gap-2 text-sm"><UserCheck className="w-4 h-4 text-emerald-400" /><span className="text-emerald-400">{prop.assigned_partner_name}</span></div><div className="text-xs text-text-secondary mt-1">Lead Price: AED {prop.lead_price || 50}</div></div>
          ) : (
            <div className="mb-3 p-2 rounded bg-yellow-500/10 border border-yellow-500/30"><div className="flex items-center gap-2 text-sm text-yellow-400"><AlertCircle className="w-4 h-4" /> Not assigned</div></div>
          )}
          <div className="flex justify-between items-center">
            <span className="text-gold font-bold">AED {prop.price?.toLocaleString()}</span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => openAssignModal(prop)} className="text-blue-400 hover:text-blue-300" data-testid={`assign-partner-${prop.id}`}><UserPlus className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" onClick={() => handleDeleteProperty(prop.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      ))}
      {properties.length === 0 && <div className="md:col-span-3 glass-card p-12 text-center text-text-secondary">No properties yet. Click <strong>Add Property</strong> to create one or bulk-import via CSV.</div>}
    </div>
  </div>
  );
};

export const AdminMarketplaceTab = ({ marketplace, handleDeleteMarketplaceItem }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {marketplace.map(item => (
        <div key={item.id} className="glass-card p-4">
          <img src={item.images?.[0] || 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=400'} alt={item.brand} className="w-full h-40 object-cover rounded mb-4" />
          <h4 className="font-semibold mb-1">{item.brand} {item.model}</h4>
          <p className="text-text-secondary text-sm mb-2">{item.category}</p>
          <div className="flex justify-between items-center">
            <span className="text-gold font-bold">AED {item.price?.toLocaleString()}</span>
            <Button variant="ghost" size="sm" onClick={() => handleDeleteMarketplaceItem(item.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></Button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const AdminTransactionsTab = ({ transactions }) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const filtered = transactions.filter(tx => {
    if (statusFilter !== 'all' && tx.payment_status !== statusFilter) return false;
    if (planFilter !== 'all' && tx.plan !== planFilter) return false;
    if (search && !(tx.user_id || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (dateFrom && new Date(tx.created_at) < new Date(dateFrom)) return false;
    if (dateTo && new Date(tx.created_at) > new Date(dateTo + 'T23:59:59')) return false;
    return true;
  });
  const totalAmount = filtered.reduce((sum, t) => sum + (t.payment_status === 'paid' ? (t.amount || 0) : 0), 0);
  return (
    <div className="space-y-6">
      <div className="glass-card p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary w-4 h-4" />
          <Input placeholder="Search user..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-white text-black" data-testid="tx-search-input" />
        </div>
        <StyledSelect value={statusFilter} onChange={(v) => setStatusFilter(v)} options={[{value:'all',label:'All Statuses'},{value:'paid',label:'Paid'},{value:'pending',label:'Pending'},{value:'cancelled',label:'Cancelled'},{value:'failed',label:'Failed'}]} className="min-w-[140px]" data-testid="tx-status-filter" />
        <StyledSelect value={planFilter} onChange={(v) => setPlanFilter(v)} options={[{value:'all',label:'All Plans'},{value:'premium',label:'Premium'},{value:'elite',label:'Elite'},{value:'free',label:'Free'}]} className="min-w-[140px]" data-testid="tx-plan-filter" />
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="bg-white text-black text-sm rounded px-3 py-2" data-testid="tx-date-from" />
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="bg-white text-black text-sm rounded px-3 py-2" data-testid="tx-date-to" />
        <div className="ml-auto text-sm text-text-secondary">Showing <span className="text-gold font-semibold">{filtered.length}</span> · Paid total: <span className="text-gold font-semibold">AED {totalAmount.toLocaleString()}</span></div>
      </div>
      <div className="glass-card overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/5"><tr>
            <th className="text-left p-4 text-text-secondary font-medium">User</th><th className="text-left p-4 text-text-secondary font-medium">Plan</th>
            <th className="text-left p-4 text-text-secondary font-medium">Amount</th><th className="text-left p-4 text-text-secondary font-medium">Status</th>
            <th className="text-left p-4 text-text-secondary font-medium">Date</th>
          </tr></thead>
          <tbody>
            {filtered.map(tx => (
              <tr key={tx.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`tx-row-${tx.id}`}>
                <td className="p-4 text-sm">{tx.user_id}</td>
                <td className="p-4"><span className={`px-2 py-1 rounded text-xs ${tx.plan === 'elite' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gold/20 text-gold'}`}>{tx.plan}</span></td>
                <td className="p-4 text-gold font-bold">AED {Number(tx.amount || 0).toLocaleString()}</td>
                <td className="p-4"><span className={`px-2 py-1 rounded text-xs ${tx.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' : tx.payment_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>{tx.payment_status}</span></td>
                <td className="p-4 text-text-secondary text-sm">{new Date(tx.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-text-secondary">No transactions match the filters</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const AdminConciergeTab = ({ conciergeRequests, setConciergeRequests }) => (
  <div className="space-y-6" data-testid="admin-concierge-panel">
    <div className="flex justify-between items-center">
      <div><h2 className="font-playfair text-2xl font-semibold">Concierge Service Requests</h2><p className="text-text-secondary text-sm mt-1">User service requests from AI concierge</p></div>
      <span className="px-3 py-1 bg-gold/10 text-gold rounded-full text-sm font-medium">{conciergeRequests.length} Requests</span>
    </div>
    {conciergeRequests.length === 0 ? (
      <div className="glass-card p-12 text-center"><MessageSquare className="w-12 h-12 mx-auto text-text-secondary mb-4 opacity-50" /><p className="text-text-secondary text-lg">No concierge service requests yet.</p></div>
    ) : (
      <div className="space-y-4">
        {conciergeRequests.map(req => (
          <div key={req.id || req.user_id} className="glass-card p-6 border border-white/10 hover:border-gold/20 transition-colors" data-testid={`concierge-request-${req.user_id}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center"><Users className="w-6 h-6 text-gold" /></div>
                <div><h3 className="font-semibold text-lg">{req.user_name || 'Unknown User'}</h3><p className="text-text-secondary text-sm flex items-center gap-2"><Mail className="w-3 h-3" /> {req.user_email || 'N/A'}</p></div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${req.status === 'pending' ? 'bg-amber-500/20 text-amber-400' : req.status === 'assigned' ? 'bg-blue-500/20 text-blue-400' : req.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/10 text-text-secondary'}`}>{(req.status || 'pending').toUpperCase()}</span>
                <Select value={req.status || 'pending'} onValueChange={async val => { try { await axios.put(`${API}/admin/concierge-requests/${req.id}`, { status: val }); setConciergeRequests(prev => prev.map(r => r.id === req.id ? { ...r, status: val } : r)); toast.success('Updated'); } catch { toast.error('Failed'); } }}>
                  <SelectTrigger className="w-32 bg-transparent border-white/20 text-sm" data-testid={`concierge-status-${req.user_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white text-black"><SelectItem value="pending">Pending</SelectItem><SelectItem value="assigned">Assigned</SelectItem><SelectItem value="in_progress">In Progress</SelectItem><SelectItem value="completed">Completed</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="mb-4"><p className="text-text-secondary text-xs uppercase tracking-wider mb-2">Requested Services</p>
              <div className="flex flex-wrap gap-2">
                {(req.requested_services || []).map(svc => <span key={svc} className="px-3 py-1 bg-gold/10 text-gold border border-gold/20 rounded-full text-sm">{svc}</span>)}
                {(!req.requested_services || req.requested_services.length === 0) && <span className="text-text-secondary text-sm">No specific services detected</span>}
              </div>
            </div>
            {req.recent_conversations?.length > 0 && (
              <div className="mb-4"><p className="text-text-secondary text-xs uppercase tracking-wider mb-2">Recent Conversations</p>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {req.recent_conversations.slice(0, 3).map((conv, i) => <div key={conv.timestamp || `conv-${i}`} className="p-3 rounded-lg bg-white/5 text-sm"><p className="text-white">{conv.message}</p><p className="text-text-secondary text-xs mt-1">{new Date(conv.timestamp).toLocaleString()}</p></div>)}
                </div>
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-white/10">
              <Input placeholder="Add admin notes..." defaultValue={req.admin_notes || ''} className="bg-transparent border-white/20" data-testid={`concierge-notes-${req.user_id}`}
                onBlur={async e => { if (e.target.value !== (req.admin_notes || '')) { try { await axios.put(`${API}/admin/concierge-requests/${req.id}`, { admin_notes: e.target.value }); setConciergeRequests(prev => prev.map(r => r.id === req.id ? { ...r, admin_notes: e.target.value } : r)); } catch {} } }} />
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);
